import { useState, useEffect, useMemo } from 'react';
import { RegistoHistorico, ClassificacaoServico } from '../page';
import ClassificacaoBadge from './ClassificacaoBadge';
import PontuacaoBar from './PontuacaoBar';
import Pagination from '@/components/base/Pagination';

interface HistoricoTableProps {
  registos: RegistoHistorico[];
  groupByFuncionario: boolean;
  onEdit: (registo: RegistoHistorico) => void;
  onDelete: (registo: RegistoHistorico) => void;
}

const avatarColors = [
  'bg-rose-100 text-rose-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-teal-100 text-teal-700',
  'bg-sky-100 text-sky-700',
  'bg-orange-100 text-orange-700',
];

function getInitials(nome: string) {
  const parts = nome.split(' ').filter((_, i, arr) => i === 0 || i === arr.length - 1);
  return parts.map((n) => n[0]).join('');
}

function getAvatarColor(id: string) {
  const idx = parseInt(id.replace(/\D/g, ''), 10) % avatarColors.length;
  return avatarColors[isNaN(idx) ? 0 : idx];
}

interface ActionCellProps {
  registo: RegistoHistorico;
  onEdit: (r: RegistoHistorico) => void;
  onDelete: (r: RegistoHistorico) => void;
}

function ActionCell({ registo, onEdit, onDelete }: ActionCellProps) {
  if (registo.fonte !== 'historico') {
    return (
      <td className="px-4 py-3.5 w-20">
        <span className="text-xs text-slate-300 italic">SIADAP</span>
      </td>
    );
  }
  return (
    <td className="px-4 py-3.5 w-20">
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onEdit(registo)}
          className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          title="Editar"
        >
          <i className="ri-edit-line text-sm" />
        </button>
        <button
          onClick={() => onDelete(registo)}
          className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
          title="Eliminar"
        >
          <i className="ri-delete-bin-line text-sm" />
        </button>
      </div>
    </td>
  );
}

// ── Vista agrupada por funcionário ──────────────────────────────────────────
function GroupedView({ registos, onEdit, onDelete }: Omit<HistoricoTableProps, 'groupByFuncionario'>) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Reset page when registos change (filters applied)
  useEffect(() => { setCurrentPage(1); }, [registos]);

  // Group by funcionário
  const grouped: Record<string, RegistoHistorico[]> = {};
  registos.forEach((r) => {
    if (!grouped[r.funcionarioId]) grouped[r.funcionarioId] = [];
    grouped[r.funcionarioId].push(r);
  });
  const groupEntries = Object.entries(grouped);
  const totalGroups = groupEntries.length;

  const safePage = Math.min(currentPage, Math.max(1, Math.ceil(totalGroups / pageSize)));
  const paginatedGroups = groupEntries.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="space-y-4">
      <div className="space-y-6">
        {paginatedGroups.map(([fid, regs]) => {
          const sorted = [...regs].sort((a, b) => b.ano - a.ano);
          const first = sorted[0];
          const colorClass = getAvatarColor(fid);
          const initials = getInitials(first.nomeFuncionario);
          return (
            <div key={fid} className="bg-white rounded-xl border border-slate-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3 bg-slate-50">
                <div className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-semibold flex-shrink-0 ${colorClass}`}>
                  {initials}
                </div>
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{first.nomeFuncionario}</p>
                  <p className="text-xs text-slate-400">{first.carreira} &middot; #{fid}</p>
                </div>
                <span className="ml-auto text-xs text-slate-400 bg-white border border-slate-200 px-2.5 py-1 rounded-full">
                  {sorted.length} registos
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[750px]">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Ano</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Pont. Chefe</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Pont. CCA</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Comparação</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Classificação de Serviço</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Fonte</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((r, i) => (
                      <tr key={r.id} className={`border-b border-slate-50 hover:bg-slate-50 transition-colors group ${i === 0 ? 'bg-emerald-50/30' : ''}`}>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            {i === 0 && (
                              <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">Último</span>
                            )}
                            <span className="font-semibold text-slate-700 text-sm">{r.ano}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-sm font-medium text-slate-700">{r.pontuacaoChefe.toFixed(1)}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-sm font-medium text-slate-700">{r.pontuacaoCCA.toFixed(1)}</span>
                        </td>
                        <td className="px-5 py-3.5 w-48">
                          <PontuacaoBar chefe={r.pontuacaoChefe} cca={r.pontuacaoCCA} />
                        </td>
                        <td className="px-5 py-3.5">
                          <ClassificacaoBadge classificacao={r.classificacaoServico} />
                        </td>
                        <td className="px-5 py-3.5">
                          {r.fonte === 'siadap' ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                              SIADAP
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                              Manual
                            </span>
                          )}
                        </td>
                        <ActionCell registo={r} onEdit={onEdit} onDelete={onDelete} />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      <Pagination
        currentPage={safePage}
        totalItems={totalGroups}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="funcionário(s)"
      />
    </div>
  );
}

type FlatSortKey = 'ano' | 'nome' | 'carreira' | 'pontosChefe' | 'pontosCCA' | 'classificacao';
type FlatSortDir = 'asc' | 'desc';

// ── Vista lista completa ────────────────────────────────────────────────────
function FlatView({ registos, onEdit, onDelete }: Omit<HistoricoTableProps, 'groupByFuncionario'>) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortKey, setSortKey] = useState<FlatSortKey>('ano');
  const [sortDir, setSortDir] = useState<FlatSortDir>('desc');

  useEffect(() => { setCurrentPage(1); }, [registos]);

  function handleSort(key: FlatSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'ano' ? 'desc' : 'asc');
    }
    setCurrentPage(1);
  }

  const CLASSIF_ORDER: Record<string, number> = {
    Excelente: 0, 'Muito Bom': 1, Bom: 2, Adequado: 3, Regular: 4, Inadequado: 5, Relevante: 6,
  };

  const sorted = useMemo(() => {
    return [...registos].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'ano': cmp = a.ano - b.ano; break;
        case 'nome': cmp = a.nomeFuncionario.localeCompare(b.nomeFuncionario); break;
        case 'carreira': cmp = (a.carreira ?? '').localeCompare(b.carreira ?? ''); break;
        case 'pontosChefe': cmp = a.pontuacaoChefe - b.pontuacaoChefe; break;
        case 'pontosCCA': cmp = a.pontuacaoCCA - b.pontuacaoCCA; break;
        case 'classificacao':
          cmp = (CLASSIF_ORDER[a.classificacaoServico] ?? 99) - (CLASSIF_ORDER[b.classificacaoServico] ?? 99);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [registos, sortKey, sortDir]);

  const safePage = Math.min(currentPage, Math.max(1, Math.ceil(sorted.length / pageSize)));
  const paginated = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  function SortIcon({ col }: { col: FlatSortKey }) {
    if (sortKey === col) {
      return <i className={`${sortDir === 'asc' ? 'ri-arrow-up-s-fill' : 'ri-arrow-down-s-fill'} text-[13px] text-slate-700`} />;
    }
    return <i className="ri-expand-up-down-line text-[11px] text-slate-300 group-hover:text-slate-400" />;
  }

  const flatHeaders: { key: FlatSortKey | null; icon: string; label: string }[] = [
    { key: 'ano', icon: 'ri-calendar-line', label: 'Ano' },
    { key: 'nome', icon: 'ri-user-line', label: 'Funcionário' },
    { key: 'carreira', icon: 'ri-briefcase-line', label: 'Carreira' },
    { key: 'pontosChefe', icon: 'ri-user-star-line', label: 'Pont. Chefe' },
    { key: 'pontosCCA', icon: 'ri-shield-check-line', label: 'Pont. CCA' },
    { key: null, icon: 'ri-bar-chart-line', label: 'Comparação' },
    { key: 'classificacao', icon: 'ri-star-line', label: 'Classificação' },
    { key: null, icon: '', label: 'Fonte' },
    { key: null, icon: '', label: 'Ações' },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                {flatHeaders.map((col, ci) => (
                  <th
                    key={ci}
                    onClick={() => col.key && handleSort(col.key)}
                    className={`px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap select-none group ${
                      col.key ? 'cursor-pointer hover:bg-zinc-100 transition-colors' : ''
                    } ${
                      sortKey === col.key ? 'text-slate-700' : 'text-slate-500'
                    }`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.icon && <i className={`${col.icon} text-sm`} />}
                      {col.label}
                      {col.key && <SortIcon col={col.key} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.map((r) => {
                const colorClass = getAvatarColor(r.funcionarioId);
                const initials = getInitials(r.nomeFuncionario);
                return (
                  <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors group">
                    <td className="px-5 py-3.5">
                      <span className="font-semibold text-slate-700 text-sm">{r.ano}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-semibold flex-shrink-0 ${colorClass}`}>
                          {initials}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800 whitespace-nowrap">{r.nomeFuncionario}</p>
                          <p className="text-xs text-slate-400">#{r.funcionarioId}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-slate-600">{r.carreira}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm font-medium text-slate-700">{r.pontuacaoChefe.toFixed(1)}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm font-medium text-slate-700">{r.pontuacaoCCA.toFixed(1)}</span>
                    </td>
                    <td className="px-5 py-3.5 w-44">
                      <PontuacaoBar chefe={r.pontuacaoChefe} cca={r.pontuacaoCCA} />
                    </td>
                    <td className="px-5 py-3.5">
                      <ClassificacaoBadge classificacao={r.classificacaoServico} />
                    </td>
                    <td className="px-5 py-3.5">
                      {r.fonte === 'siadap' ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                          SIADAP
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                          Manual
                        </span>
                      )}
                    </td>
                    <ActionCell registo={r} onEdit={onEdit} onDelete={onDelete} />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={safePage}
        totalItems={sorted.length}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="registo(s)"
      />
    </div>
  );
}

// ── Export principal ────────────────────────────────────────────────────────
export default function HistoricoTable({ registos, groupByFuncionario, onEdit, onDelete }: HistoricoTableProps) {
  if (registos.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-100 py-20 flex flex-col items-center gap-3 text-slate-400">
        <div className="w-12 h-12 flex items-center justify-center">
          <i className="ri-search-line text-4xl" />
        </div>
        <p className="text-sm">Nenhum registo encontrado com os filtros aplicados.</p>
      </div>
    );
  }

  return groupByFuncionario
    ? <GroupedView registos={registos} onEdit={onEdit} onDelete={onDelete} />
    : <FlatView registos={registos} onEdit={onEdit} onDelete={onDelete} />;
}
